import { useEffect, useState } from 'react';
import type { BuddyApprovalMode } from '@kidzim/shared';
import { Card, ErrorState, Switch } from '../design-system/components';
import { api, type ApiError } from '../lib/api';

interface FamilySettings {
  allowLinks: boolean;
  allowImages: boolean;
  allowEmoji: boolean;
  buddyApprovalMode: BuddyApprovalMode;
  timezone: string;
}

/** Every IANA zone the browser knows, falling back to a short common list on
 *  older browsers that lack `Intl.supportedValuesOf`. */
function timeZoneOptions(): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  if (supported) return supported('timeZone');
  return ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London'];
}

export function Settings() {
  const [settings, setSettings] = useState<FamilySettings | null>(null);
  const [saved, setSaved] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  function load() {
    setLoadError(null);
    api<{ family: { settings: FamilySettings } }>('/api/family')
      .then((d) => setSettings(d.family.settings))
      .catch((err) => setLoadError(err as ApiError));
  }
  useEffect(() => {
    load();
  }, []);

  async function update(patch: Partial<FamilySettings>) {
    if (!settings) return;
    const next = { ...settings, ...patch };
    setSettings(next);
    await api('/api/family/settings', { method: 'PUT', body: JSON.stringify(next) });
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  if (loadError) {
    const message =
      loadError.error === 'forbidden'
        ? "This page is for parents managing a family. As an admin, you don't have a family here."
        : loadError.error === 'no_family'
          ? "You don't currently belong to a family. Ask another grown-up in your household for an invite code, or contact support."
          : 'Could not load safety settings.';
    return <ErrorState message={message} onRetry={loadError.error === 'forbidden' || loadError.error === 'no_family' ? undefined : load} />;
  }
  if (!settings) return <p className="kz-muted">Loading…</p>;

  return (
    <div className="kz-stack" style={{ gap: 'var(--space-6)', maxWidth: 560 }}>
      <div>
        <span className="kz-eyebrow">Safety settings</span>
        <h1 style={{ marginTop: 'var(--space-1)' }}>Defaults for new rooms</h1>
        <p className="kz-muted">These choices seed every new room. You can still adjust each room when you create it.</p>
      </div>

      <Card className="kz-stack" style={{ gap: 'var(--space-4)' }}>
        <Switch label="Allow links in messages" checked={settings.allowLinks} onChange={(v) => update({ allowLinks: v })} />
        <Switch label="Allow pictures in messages" checked={settings.allowImages} onChange={(v) => update({ allowImages: v })} />
        <Switch label="Allow emoji" checked={settings.allowEmoji} onChange={(v) => update({ allowEmoji: v })} />
        {saved && <p style={{ color: 'var(--safe-700)', font: 'var(--type-small)' }}>Saved ✓</p>}
      </Card>

      <Card style={{ background: 'var(--blue-50)', borderColor: 'var(--blue-100)' }}>
        <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
          🛡️ Phone numbers and email addresses are always hidden automatically — even when links are allowed — and flagged for you to review.
        </p>
      </Card>

      <div>
        <span className="kz-eyebrow">Household</span>
        <h2 style={{ marginTop: 'var(--space-1)' }}>Time zone</h2>
        <p className="kz-muted">Used to work out each day's start and end for buddy-chat time limits.</p>
      </div>

      <Card>
        <select
          value={settings.timezone}
          onChange={(e) => update({ timezone: e.target.value })}
          style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', border: 'var(--border-width) solid var(--border-default)', font: 'var(--type-body)', width: '100%' }}
        >
          {timeZoneOptions().map((tz) => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </select>
        {saved && <p style={{ color: 'var(--safe-700)', font: 'var(--type-small)', marginTop: 'var(--space-2)' }}>Saved ✓</p>}
      </Card>

      <div>
        <span className="kz-eyebrow">Buddies</span>
        <h2 style={{ marginTop: 'var(--space-1)' }}>Who approves new buddies</h2>
        <p className="kz-muted">
          When another family's kid asks to befriend yours, choose whether you also need to approve, or whether the
          asking child's own parent is enough. Buddies within your own family always connect automatically.
        </p>
      </div>

      <Card className="kz-stack" style={{ gap: 'var(--space-3)' }}>
        <ModeChoice
          checked={settings.buddyApprovalMode === 'both_parents'}
          onSelect={() => update({ buddyApprovalMode: 'both_parents' })}
          title="Both parents approve"
          desc="The safest option. You must approve any request to connect with your child (recommended)."
        />
        <ModeChoice
          checked={settings.buddyApprovalMode === 'requesting_only'}
          onSelect={() => update({ buddyApprovalMode: 'requesting_only' })}
          title="Only the asking child's parent"
          desc="You pre-approve incoming requests; the other child's parent still approves on their side."
        />
        {saved && <p style={{ color: 'var(--safe-700)', font: 'var(--type-small)' }}>Saved ✓</p>}
      </Card>
    </div>
  );
}

function ModeChoice({
  checked,
  onSelect,
  title,
  desc,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  desc: string;
}) {
  return (
    <label className="kz-row" style={{ gap: 'var(--space-3)', alignItems: 'flex-start', cursor: 'pointer' }}>
      <input type="radio" name="buddyApprovalMode" checked={checked} onChange={onSelect} style={{ marginTop: 4 }} />
      <span>
        <span style={{ font: 'var(--type-body-strong)', display: 'block' }}>{title}</span>
        <span className="kz-muted" style={{ font: 'var(--type-small)' }}>{desc}</span>
      </span>
    </label>
  );
}
